import React, { useEffect, useState } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

import host from "../../../../global.jsx";
import StepIndicator from "../../mcq/StepIndicator";
import GeneratingLoader from "./GeneratingLoader";
import BehavioralAIStep1_Requirements from "./BehavioralAIStep1_Requirements";
import BehavioralAIStep2_Allocation from "./BehavioralAIStep2_Allocation";
import { DEFAULT_CONFIG } from "../manual/SetDetailsCard";

// This component only ever renders steps 1-2. Step 3 lives on CreateBehavioralSet.jsx
// (own copy of this array), keeping the indicator continuous across both pages.
const AI_STEPS = ["Requirements", "Review Allocation", "Edit & Save"];

/**
 * The AI-guided creation flow (behavioral-test-phase2-ai-guided-proposal.md).
 * Deliberately no separate review/save screen after generation — Generate hands off
 * straight into CreateBehavioralSet.jsx's already-editable builder instead of a
 * read-only review step (the divergence from MCQ's AIStep3_Generate documented in
 * the proposal), which is why this component's own step state only ever reaches 2.
 */
const AIGenerateFlow = () => {
  const navigate = useNavigate();
  const accessToken = Cookies.get("accessToken");
  const authHeaders = { Authorization: `Bearer ${accessToken}` };

  const [step, setStep] = useState(1);

  const [source, setSource] = useState("role");
  const [jobRole, setJobRole] = useState("");
  const [jdId, setJdId] = useState(null);
  const [jdList, setJdList] = useState([]);
  const [isJdListLoading, setIsJdListLoading] = useState(false);
  const [timeBudget, setTimeBudget] = useState(15);
  const [testName, setTestName] = useState("");

  const [isRecommending, setIsRecommending] = useState(false);
  const [allocation, setAllocation] = useState([]);
  const [targetQuestions, setTargetQuestions] = useState(0);
  // All four bounds come from the /recommend response (behavioral_ai_generation.py's
  // build_recommendation, sourced from app/constants/behavioral_role_categories.py) —
  // read from the backend rather than duplicated as frontend literals, so a bound
  // change on the server can't silently drift out of sync with what this screen
  // enforces. Defaults here only cover the brief window before the first successful
  // /recommend call.
  const [minQuestionsPerCompetency, setMinQuestionsPerCompetency] = useState(3);
  const [maxQuestionsPerCompetency, setMaxQuestionsPerCompetency] = useState(8);
  const [minCompetencies, setMinCompetencies] = useState(2);
  const [maxCompetencies, setMaxCompetencies] = useState(5);

  const [isGenerating, setIsGenerating] = useState(false);
  const [totalFailure, setTotalFailure] = useState(false);

  const requestPayload = () => ({
    jobrole: source === "role" ? jobRole.trim() : null,
    jd_id: source === "jd" ? jdId : null,
  });

  // Fetched once, eagerly, on mount — the combined role/JD autocomplete on Step 1
  // has no separate "switch to JD mode" trigger to hang a lazy fetch off anymore,
  // and needs the list ready the moment the recruiter opens the dropdown.
  useEffect(() => {
    let cancelled = false;
    const loadJds = async () => {
      setIsJdListLoading(true);
      try {
        const res = await axios.get(`${host}/orgadmin/get_jd_data`, { headers: authHeaders });
        if (!cancelled) setJdList(res.data.job_descriptions || []);
      } catch {
        if (!cancelled) toast.error("Could not load job descriptions");
      } finally {
        if (!cancelled) setIsJdListLoading(false);
      }
    };
    loadJds();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRecommend = async () => {
    setIsRecommending(true);
    try {
      const res = await axios.post(
        `${host}/behavioral-sets/recommend`,
        { ...requestPayload(), time_budget_minutes: timeBudget },
        { headers: authHeaders }
      );
      setAllocation(res.data.allocation || []);
      setTargetQuestions(res.data.target_questions || 0);
      setMinQuestionsPerCompetency(res.data.min_questions_per_competency || 3);
      setMaxQuestionsPerCompetency(res.data.max_questions_per_competency || 8);
      setMinCompetencies(res.data.min_competencies || 2);
      setMaxCompetencies(res.data.max_competencies || 5);
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error getting recommendation");
    } finally {
      setIsRecommending(false);
    }
  };

  const handleToggle = (competencyId) => {
    setAllocation((prev) =>
      prev.map((e) =>
        e.competency_id === competencyId
          ? { ...e, included: !e.included, count: !e.included ? Math.max(minQuestionsPerCompetency, e.count) : e.count }
          : e
      )
    );
  };

  const handleCountChange = (competencyId, count) => {
    setAllocation((prev) => prev.map((e) => (e.competency_id === competencyId ? { ...e, count } : e)));
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    setTotalFailure(false);
    try {
      const included = allocation
        .filter((e) => e.included)
        .map((e) => ({ competency_id: e.competency_id, count: e.count }));
      const res = await axios.post(
        `${host}/behavioral-sets/generate`,
        { ...requestPayload(), allocation: included },
        { headers: authHeaders }
      );
      if (res.data.total_failure) {
        setTotalFailure(true);
        return;
      }
      // The backend's failed_competencies doesn't echo back the requested count —
      // enrich it here from `included` (what this request actually sent) so a
      // later single-competency retry on the builder screen can ask for the same
      // count again, not an arbitrary default.
      const failedWithCount = (res.data.failed_competencies || []).map((f) => ({
        ...f,
        count: included.find((e) => e.competency_id === f.competency_id)?.count || minQuestionsPerCompetency,
      }));
      // Seed the time limit from what actually got delivered, not the original
      // Step 1 budget — Step 2 can only ever reduce the confirmed total below
      // target (never raise it past target_questions), and a partial generation
      // failure can shrink it further still, so timeBudget alone can be stale by
      // the time a draft lands here. 1.5 min/question matches the backend's own
      // MINUTES_PER_QUESTION (behavioral_ai_generation.py) — same rate, run in
      // reverse. Still just the seed value: the recruiter can freely edit it on
      // the builder screen afterward, same as every other field.
      const deliveredCount = res.data.questions?.length || 0;
      const seededTimeLimit = deliveredCount ? Math.round(deliveredCount * 1.5) : timeBudget;
      // Set name defaults to the role name (Step 1's own subtitle promises this) —
      // the JD-sourced case has no free-text role string of its own, so it's looked
      // up from the same jdList the picker already has in hand.
      const roleNameForDefault = source === "role"
        ? jobRole.trim()
        : (jdList.find((jd) => jd.jd_id === jdId)?.jobrole || "");
      navigate("/org-admin/behavioralsets/new", {
        state: {
          aiDraft: {
            name: testName.trim() || roleNameForDefault,
            description: "",
            questions: res.data.questions || [],
            config: { ...DEFAULT_CONFIG, time_limit_minutes: seededTimeLimit },
            source: "ai_guided",
          },
          failedCompetencies: failedWithCount,
          aiRequestContext: requestPayload(),
        },
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error generating questions");
    } finally {
      setIsGenerating(false);
    }
  };

  const goToStep = (n) => { if (n < step) setStep(n); };

  const handleBack = () => {
    if (totalFailure) { setTotalFailure(false); return; }
    if (step > 1) { setStep((s) => s - 1); return; }
    navigate("/org-admin/behavioralsets");
  };

  return (
      <div className="min-h-screen">
        <div className="bg-bgPrimary flex flex-col min-h-screen border border-gray-700/50 rounded-2xl pt-4 pb-16">
          <div className="flex items-center gap-2 px-6 pt-2 mb-2">
            <button
              onClick={handleBack}
              className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 dark:text-[#C9A3FF] text-textSecondary" />
            </button>
            <span className="dark:text-gray-400 text-textSecondary">
              Generate Behavioural Set with AI
            </span>
          </div>

          <div className="w-full flex-1 mt-4">
            <div className="space-y-6">
              {!totalFailure && (
                <StepIndicator steps={AI_STEPS} currentStep={step} onStepClick={goToStep} />
              )}

              {totalFailure ? (
                <div className="px-12">
                  <div className="max-w-lg mx-auto rounded-2xl border border-amber-400/30 bg-amber-500/10 p-8 text-center space-y-4">
                    <p className="text-sm font-medium text-amber-400">
                      Generation failed for every competency
                    </p>
                    <p className="text-xs dark:text-gray-400 text-textSecondary">
                      This can happen if the AI provider is temporarily unavailable. You can start
                      from the platform's default question set instead — it's fully editable too.
                    </p>
                    <button
                      onClick={() => navigate("/org-admin/behavioralsets/new")}
                      className="button"
                    >
                      Use the default question set instead
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {step === 1 && (
                    <div className="px-12">
                      <BehavioralAIStep1_Requirements
                        source={source} setSource={setSource}
                        jobRole={jobRole} setJobRole={setJobRole}
                        jdId={jdId} setJdId={setJdId}
                        jdList={jdList} isJdListLoading={isJdListLoading}
                        timeBudget={timeBudget} setTimeBudget={setTimeBudget}
                        testName={testName} setTestName={setTestName}
                        onNext={handleRecommend}
                        onBack={handleBack}
                        isLoading={isRecommending}
                      />
                    </div>
                  )}

                  {step === 2 && (
                    <div className="px-12">
                      {isGenerating ? (
                        <GeneratingLoader />
                      ) : (
                        <BehavioralAIStep2_Allocation
                          allocation={allocation}
                          targetQuestions={targetQuestions}
                          minQuestionsPerCompetency={minQuestionsPerCompetency}
                          maxQuestionsPerCompetency={maxQuestionsPerCompetency}
                          minCompetencies={minCompetencies}
                          maxCompetencies={maxCompetencies}
                          timeBudget={timeBudget}
                          onToggle={handleToggle}
                          onCountChange={handleCountChange}
                          onGenerate={handleGenerate}
                          onBack={handleBack}
                          isGenerating={isGenerating}
                        />
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
  );
};

export default AIGenerateFlow;
